const TRUE_VALUES = new Set(["1", "true", "yes", "y", "on"]);
const FALSE_VALUES = new Set(["0", "false", "no", "n", "off"]);
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const RESTRICTED_VISIBILITY_VALUES = new Set([
  "hidden",
  "internal",
  "invite",
  "invite-only",
  "invitation-only",
  "members",
  "members-only",
  "password",
  "private",
  "token",
  "token-only",
  "unlisted",
]);

const RESTRICTED_ACCESS_VALUES = new Set([
  "hidden",
  "internal",
  "invite",
  "invite-only",
  "invitation",
  "invitation-only",
  "jwt",
  "member",
  "members",
  "members-only",
  "password",
  "private",
  "role",
  "token",
  "token-only",
]);

const PUBLIC_ACCESS_VALUES = new Set(["none", "open", "public"]);

const INELIGIBLE_STATUS_VALUES = new Set([
  "archived",
  "canceled",
  "cancelled",
  "deleted",
  "draft",
  "hidden",
  "inactive",
  "internal",
  "noindex",
  "nonindex",
  "pending",
  "private",
  "rejected",
  "removed",
  "review",
  "unlisted",
  "unpublished",
]);

const RESTRICTED_POLICY_TERMS = new Set([
  "hidden",
  "internal",
  "invitation",
  "invite",
  "private",
  "token",
  "unlisted",
]);

const INELIGIBLE_STATUS_TERMS = new Set([
  "archived",
  "canceled",
  "cancelled",
  "deleted",
  "draft",
  "hidden",
  "inactive",
  "internal",
  "noindex",
  "pending",
  "private",
  "rejected",
  "removed",
  "review",
  "unlisted",
  "unpublished",
]);

const RESTRICTED_TRUE_FLAGS = [
  "private",
  "isPrivate",
  "is_private",
  "hidden",
  "isHidden",
  "is_hidden",
  "unlisted",
  "isUnlisted",
  "is_unlisted",
  "internal",
  "isInternal",
  "is_internal",
  "requiresAuth",
  "requires_auth",
  "authRequired",
  "auth_required",
  "loginRequired",
  "login_required",
  "requiresLogin",
  "requires_login",
  "requiresToken",
  "requires_token",
  "tokenProtected",
  "token_protected",
  "tokenOnly",
  "token_only",
  "inviteOnly",
  "invite_only",
  "invitationOnly",
  "invitation_only",
  "internalOnly",
  "internal_only",
  "membersOnly",
  "members_only",
  "deleted",
  "isDeleted",
  "is_deleted",
  "removed",
  "isRemoved",
  "is_removed",
  "canceled",
  "cancelled",
  "isCanceled",
  "is_canceled",
  "noindex",
  "isNoindex",
  "is_noindex",
  "seo.noindex",
  "seo.noIndex",
  "meta.noindex",
  "meta.noIndex",
];

const REQUIRED_TRUE_FLAGS = [
  "enabled",
  "isEnabled",
  "is_enabled",
  "published",
  "isPublished",
  "is_published",
  "discoverable",
  "isDiscoverable",
  "is_discoverable",
  "searchable",
  "isSearchable",
  "is_searchable",
  "listed",
  "isListed",
  "is_listed",
  "publiclyVisible",
  "isPubliclyVisible",
  "is_publicly_visible",
  "indexable",
  "isIndexable",
  "is_indexable",
  "seo.indexable",
  "seo.isIndexable",
  "meta.indexable",
  "meta.isIndexable",
  "public",
  "isPublic",
  "is_public",
];

function getByPath(record: Record<string, unknown>, path: string): unknown {
  let value: unknown = record;

  for (const segment of path.split(".")) {
    if (!value || typeof value !== "object" || Array.isArray(value))
      return undefined;
    value = (value as Record<string, unknown>)[segment];
  }

  return value;
}

function valuesByPath(
  record: Record<string, unknown>,
  keys: string[],
): unknown[] {
  return keys.flatMap((key) => {
    const value = getByPath(record, key);
    return value === undefined || value === null ? [] : [value];
  });
}

function normalizedStrings(
  record: Record<string, unknown>,
  keys: string[],
): string[] {
  return valuesByPath(record, keys)
    .map((value) => String(value).trim().toLowerCase())
    .filter(Boolean);
}

function normalizedPolicyValues(
  record: Record<string, unknown>,
  keys: string[],
): string[] {
  return valuesByPath(record, keys)
    .map((value) =>
      String(value)
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
        .toLowerCase()
        .replace(/[\s_]+/g, "-")
        .replace(/-+/g, "-"),
    )
    .filter(Boolean);
}

function matchesPolicy(
  value: string,
  values: Set<string>,
  terms: Set<string>,
): boolean {
  return values.has(value) || value.split("-").some((term) => terms.has(term));
}

function matchesIneligibleStatus(value: string): boolean {
  const terms = value.split("-");
  return (
    matchesPolicy(value, INELIGIBLE_STATUS_VALUES, INELIGIBLE_STATUS_TERMS) ||
    value.replaceAll("-", "").includes("unpublished") ||
    (terms.includes("not") && terms.includes("published"))
  );
}

export function readBooleanLike(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
    return undefined;
  }

  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (TRUE_VALUES.has(normalized)) return true;
  if (FALSE_VALUES.has(normalized)) return false;
  return undefined;
}

export function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function hasExplicitTrue(
  record: Record<string, unknown>,
  keys: string[],
): boolean {
  return keys.some((key) => readBooleanLike(getByPath(record, key)) === true);
}

function hasExplicitFalse(
  record: Record<string, unknown>,
  keys: string[],
): boolean {
  return keys.some((key) => readBooleanLike(getByPath(record, key)) === false);
}

function hasCredential(record: Record<string, unknown>): boolean {
  return [
    "accessToken",
    "access_token",
    "token",
    "inviteToken",
    "invite_token",
    "invitationToken",
    "invitation_token",
    "inviteCode",
    "invite_code",
  ].some((key) => {
    const value = getByPath(record, key);
    return (
      value !== undefined && value !== null && String(value).trim().length > 0
    );
  });
}

export function isPublicDiscoveryRecord(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;

  if (hasExplicitTrue(record, RESTRICTED_TRUE_FLAGS)) return false;
  if (hasExplicitFalse(record, REQUIRED_TRUE_FLAGS)) return false;
  if (hasCredential(record)) return false;

  const visibilityValues = normalizedPolicyValues(record, [
    "visibility",
    "privacy",
    "accessLevel",
    "access_level",
  ]);
  if (
    visibilityValues.some((value) =>
      matchesPolicy(
        value,
        RESTRICTED_VISIBILITY_VALUES,
        RESTRICTED_POLICY_TERMS,
      ),
    )
  ) {
    return false;
  }

  const accessModes = normalizedPolicyValues(record, [
    "access",
    "privateAccessMode",
    "private_access_mode",
    "accessMode",
    "access_mode",
    "authMode",
    "auth_mode",
  ]);
  if (
    accessModes.some(
      (mode) =>
        matchesPolicy(
          mode,
          RESTRICTED_ACCESS_VALUES,
          RESTRICTED_POLICY_TERMS,
        ) || !PUBLIC_ACCESS_VALUES.has(mode),
    )
  )
    return false;

  const statuses = normalizedPolicyValues(record, [
    "status",
    "state",
    "publishStatus",
    "publish_status",
    "publishingStatus",
    "publishing_status",
  ]);
  if (statuses.some(matchesIneligibleStatus)) {
    return false;
  }

  const deletionTimestamps = normalizedStrings(record, [
    "deletedAt",
    "deleted_at",
    "deleteAt",
    "delete_at",
    "deletedDate",
    "deleted_date",
    "deletedOn",
    "deleted_on",
    "deletedTimestamp",
    "deleted_timestamp",
    "deletionDate",
    "deletion_date",
    "deletionAt",
    "deletion_at",
    "deletionTimestamp",
    "deletion_timestamp",
    "removedAt",
    "removed_at",
    "removedOn",
    "removed_on",
    "removedTimestamp",
    "removed_timestamp",
    "removalDate",
    "removal_date",
    "removalTimestamp",
    "removal_timestamp",
  ]);
  if (deletionTimestamps.length > 0) return false;

  const robotsValues = normalizedStrings(record, [
    "robots",
    "robotsDirective",
    "robots_directive",
    "seo.robots",
    "meta.robots",
  ]);
  if (
    robotsValues.some((robots) =>
      robots
        .split(/[\s,]+/)
        .some((directive) => directive === "noindex" || directive === "none"),
    )
  ) {
    return false;
  }

  return true;
}

export function filterPublicDiscoveryRecords<T>(records: T[]): T[] {
  return records.filter(isPublicDiscoveryRecord);
}
